import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import LightModeIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeIcon from "@mui/icons-material/DarkModeOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlineOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import { useNavigate } from "react-router-dom";
import BrandLogo from "./BrandLogo";
import { useColorMode } from "../../theme/ColorModeProvider";
import { useShell } from "./ShellContext";
import { getDisplayName, getInitials, getRoleName, getPortal, logout } from "../../utils/auth";
import { layout } from "../../theme/tokens";

export default function TopBar({
  isMobile,
  sidebarCollapsed,
  onToggleSidebar,
  title,
  backTo,
}) {
  const { setActionsSlot } = useShell();
  const navigate = useNavigate();
  const { resolvedMode, toggleMode } = useColorMode();
  const [anchorEl, setAnchorEl] = useState(null);

  const displayName = getDisplayName();
  const roleName = getRoleName();

  const themeToggle = (
    <Tooltip title={resolvedMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
      <IconButton onClick={toggleMode} size="small" sx={{ color: "text.secondary" }}>
        {resolvedMode === "dark" ? (
          <LightModeIcon sx={{ fontSize: 20 }} />
        ) : (
          <DarkModeIcon sx={{ fontSize: 20 }} />
        )}
      </IconButton>
    </Tooltip>
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        // Desktop: sits to the right of the sidebar. Mobile: full width.
        width: isMobile
          ? "100%"
          : `calc(100% - ${sidebarCollapsed ? layout.sidebarCollapsedWidth : layout.sidebarWidth}px)`,
        ml: isMobile ? 0 : `${sidebarCollapsed ? layout.sidebarCollapsedWidth : layout.sidebarWidth}px`,
        transition: (t) =>
          t.transitions.create(["width", "margin-left"], {
            duration: t.transitions.duration.shorter,
          }),
        pt: isMobile ? "env(safe-area-inset-top)" : 0,
        // Phones have no sidebar, so the brand colour lives in the header
        // there; on desktop the navy rail already carries it.
        ...(isMobile && {
          bgcolor: "nav.bg",
          color: "nav.fg",
          borderBottom: 0,
          backdropFilter: "none",
        }),
      }}
    >
      <Toolbar sx={{ minHeight: `${layout.topBarHeight}px !important`, gap: 1, px: { xs: 1, sm: 2 } }}>
        {isMobile ? (
          backTo ? (
            <IconButton
              edge="start"
              onClick={() => (typeof backTo === "string" ? navigate(backTo) : navigate(-1))}
              sx={{ color: "inherit" }}
              aria-label="Back"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
            </IconButton>
          ) : (
            <BrandLogo height={24} color="nav.fg" sx={{ ml: 1, mr: 0.5 }} />
          )
        ) : (
          <Tooltip title={sidebarCollapsed ? "Expand menu" : "Collapse menu"}>
            <IconButton edge="start" onClick={onToggleSidebar} sx={{ color: "text.secondary" }}>
              {sidebarCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
            </IconButton>
          </Tooltip>
        )}

        {/* On desktop the page renders its own <PageHeader> title, so repeating
            it here just stacks the same words twice. */}
        {isMobile ? (
          <Typography variant="h6" noWrap sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }}>
            {title}
          </Typography>
        ) : (
          <Box sx={{ flexGrow: 1 }} />
        )}

        {/* Screens portal their mobile header actions in here (see PageHeader). */}
        <Box ref={setActionsSlot} sx={{ display: "flex", alignItems: "center", gap: 0.5 }} />

        {!isMobile && themeToggle}

        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
          sx={{ p: 0.5 }}
          aria-label="Account menu"
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "primary.contrastText",
              bgcolor: "primary.main",
            }}
          >
            {getInitials()}
          </Avatar>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { minWidth: 220 } } }}
        >
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography variant="subtitle2" noWrap>
              {displayName}
            </Typography>
            {roleName && (
              <Typography variant="caption" color="text.secondary">
                {roleName}
              </Typography>
            )}
          </Box>
          <Divider sx={{ my: 0.5 }} />
          {isMobile && (
            <MenuItem
              onClick={() => {
                toggleMode();
                setAnchorEl(null);
              }}
            >
              <ListItemIcon>
                {resolvedMode === "dark" ? (
                  <LightModeIcon sx={{ fontSize: 19 }} />
                ) : (
                  <DarkModeIcon sx={{ fontSize: 19 }} />
                )}
              </ListItemIcon>
              {resolvedMode === "dark" ? "Light mode" : "Dark mode"}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              navigate(`${getPortal() === "admin" ? "/admin" : "/user"}/profile`);
            }}
          >
            <ListItemIcon>
              <PersonIcon sx={{ fontSize: 19 }} />
            </ListItemIcon>
            View Profile
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              logout(navigate);
            }}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <LogoutIcon sx={{ fontSize: 19, color: "error.main" }} />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
